import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../redux/slices/common";
import { STILO } from "utilities/constants";
import { getData } from "utilities/common";

const CommonQuillEditor = ({
  value,
  disabled = false,
  onChange,
  allowImage = true,
  allowHeading = true,
  style = false,
  height = "400px",
  strictMode = false, // true: Block entire paste | false: Paste text only (remove image)
}) => {
  const [editorHtml, setEditorHtml] = useState("");
  const adminsMode = getData("adminsMode");
  const isStilo = JSON.parse(adminsMode) === STILO;
  const dispatch = useDispatch();
  const quillRef = useRef(null);
  const MAX_FILE_SIZE = 500 * 1024;

  const getBase64Size = (base64String) => {
    if (!base64String.includes(",")) return 0;
    const stringLength = base64String.split(",")[1]?.length || 0;
    return (stringLength * 3) / 4;
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
        allowHeading
          ? [{ header: [1, 2, 3, 4, 5, 6, false] }]
          : [{ header: [2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        allowImage ? ["link", "image"] : ["link"],
        ["clean"],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ align: [] }],
      ],
      },
      clipboard: {
        matchVisual: false,
        matchers: [
          [
            "img",
            (node, delta) => {
              const src = node.getAttribute("src");
              const MAX_SIZE = 500 * 1024;

              if (src && src.startsWith("data:image")) {
                const size = getBase64Size(src);
                if (size > MAX_SIZE) {
                  return { ops: [] };
                }
              }
              return delta;
            },
          ],
        ],
      },
    }),
    [allowHeading, allowImage, dispatch, strictMode]
  );

  // Handle the paste logic
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const handlePaste = (e) => {
      const clipboardItems = e.clipboardData?.items;
      let hasLargeImage = false;

      // Check direct image files
      if (clipboardItems) {
        for (let item of clipboardItems) {
          if (item.type.startsWith("image")) {
            const file = item.getAsFile();
            if (file && file.size > MAX_FILE_SIZE) {
              hasLargeImage = true;
              break;
            }
          }
        }
      }

      // Check HTML images
      const html = e.clipboardData?.getData("text/html");
      if (html && !hasLargeImage) {
        const imgMatches = html.match(/<img[^>]+src="([^"]+)"/g) || [];

        for (let imgTag of imgMatches) {
          const srcMatch = imgTag.match(/src="([^"]+)"/);
          const src = srcMatch?.[1];

          if (src && src.startsWith("data:image")) {
            const size = getBase64Size(src);
            if (size > MAX_FILE_SIZE) {
              hasLargeImage = true;
              break;
            }
          }
        }
      }

      if (hasLargeImage) {
        dispatch(
          openSnackbar({
            content: strictMode
              ? `Image size must be less than 500KB`
              : "Image size exceeds 500KB, removed from content",
            color: "error",
            icon: "warning",
          })
        );

        if (strictMode) {
          e.preventDefault();
        }
      }
    };

    quill.root.addEventListener("paste", handlePaste);
    return () => {
      quill.root.removeEventListener("paste", handlePaste);
    };
  }, [dispatch, strictMode]);

  useEffect(() => {
  setEditorHtml(value || "");
}, [value]);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file || file.size > MAX_FILE_SIZE) {
        dispatch(openSnackbar({ content: "Image must be under 500KB", color: "error" }));
        return;
      }
      const formData = new FormData();
      formData.append("filePath", file);
      try {
        const { uploadImage } = await import("../../redux/slices/common");
        const result = await dispatch(uploadImage(formData)).unwrap();
        if (result?.fileUrl) {
          const quill = quillRef.current?.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", result.fileUrl);
        }
      } catch (e) {}
    };
  }, [dispatch]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) quill.getModule("toolbar").addHandler("image", imageHandler);
  }, [imageHandler]);

  return (
    <div className="mb-4">
      <ReactQuill
        ref={quillRef}
        className="quill-text-editor"
        theme="snow"
        value={editorHtml}
        readOnly={disabled}
        onChange={(html) => {
          setEditorHtml(html);
          onChange?.(html);
        }}
        modules={modules}
        formats={[
          "header",
          "font",
          "size",
          "bold",
          "italic",
          "underline",
          "strike",
          "blockquote",
          "list",
          "bullet",
          "indent",
          "link",
          "image",
          "color",
          "background",
          "align",
        ]}
        style={
          isStilo && style
            ? {
                height: height,
                display: "flex",
                flexDirection: "column",
              }
            : undefined
        }
      />
    </div>
  );
};

export default CommonQuillEditor;